import { IPart } from "../../../models/Part";

const EBAY_TITLE_MAX = 80;

function collapseSpace(s: string) {
  return s.trim().replace(/\s{2,}/g, " ");
}

function clip(s: string) {
  const t = collapseSpace(s);
  if (t.length <= EBAY_TITLE_MAX) return t;
  return t.slice(0, EBAY_TITLE_MAX).replace(/\s+\S*$/, "").trim() || t.slice(0, EBAY_TITLE_MAX);
}

export type EbayTitleSuggestion = {
  id: string;
  reason: string;
  title: string;
};

/**
 * Heuristic, client-side eBay title variants (SEO-oriented). No network — replace
 * with an API when title intelligence is available server-side.
 */
export function buildEbayTitleIntelligenceSuggestions(
  part: IPart,
  currentTitle: string
): EbayTitleSuggestion[] {
  const y = part.vehicle?.dvlaYearOfManufacture;
  const make = part.vehicle?.combinedMake?.trim() ?? "";
  const model = part.vehicle?.combinedModel?.trim() ?? "";
  const shortModel = part.vehicle?.modelRangeDescription?.trim() ?? model;
  const category = part.category?.name?.trim() ?? "";
  const mpn = (part.oePartNumber ?? part.partNumber)?.trim() ?? "";
  const cur = collapseSpace(currentTitle);
  const lower = cur.toLowerCase();

  const out: EbayTitleSuggestion[] = [];
  const seen = new Set<string>();

  const add = (id: string, reason: string, title: string) => {
    const t = clip(title);
    if (!t || seen.has(t)) return;
    seen.add(t);
    out.push({ id, reason, title: t });
  };

  add("tidy", "Clean spacing", cur);

  const candidateYearFirst =
    y && make && shortModel && category
      ? `${y} ${make} ${shortModel} ${category}`
      : "";
  if (
    candidateYearFirst &&
    clip(candidateYearFirst) !== cur &&
    clip(candidateYearFirst).toLowerCase() !== lower
  ) {
    add(
      "year_first",
      "Year before make & model (search-friendly order)",
      candidateYearFirst
    );
  }

  if (mpn && mpn.length > 0 && !lower.includes(mpn.toLowerCase())) {
    const withMpn = clip(`${cur} ${mpn}`);
    if (withMpn !== cur && withMpn.length <= EBAY_TITLE_MAX) {
      add(
        "include_mpn",
        "Add OEM / part number for exact-match searches",
        withMpn
      );
    }
  }

  if (!/\boem\b/i.test(cur) && !/\bgenuine\b/i.test(cur) && cur.length < EBAY_TITLE_MAX - 4) {
    const withOem = clip(`${cur} OEM`);
    if (withOem !== cur) {
      add("oem_tag", "OEM keyword (common on marketplace titles)", withOem);
    }
  }

  if (category && !lower.includes(category.toLowerCase()) && y && make) {
    const withCat = clip(`${y} ${make} ${category}`);
    if (withCat !== cur && withCat.length <= EBAY_TITLE_MAX) {
      add(
        "fitment_snippet",
        "Year, make & part type in one line",
        withCat
      );
    }
  }

  if (!/\bused\b/i.test(cur) && cur.length < EBAY_TITLE_MAX - 5) {
    const withUsed = clip(`Used ${cur}`);
    if (withUsed !== cur) {
      add("used_prefix", "“Used” in front (condition for parts buyers)", withUsed);
    }
  }

  if (out.length === 0) {
    const fromPart = clip(
      [y, make, shortModel, category].filter(Boolean).join(" ")
    );
    if (fromPart) {
      add(
        "from_part",
        "From year, make, model & part type on this part",
        fromPart
      );
    }
  }

  return out.slice(0, 8);
}
